<template>
    <form
        :class="{ 'padded-container': isEditPayment }"
        class="payment-layout__container"
        ref="paymentForm"
        v-on:submit.stop.prevent>
        <div
            class="aa-layout payment-layout--edit"
            v-bind:class="[
                { 'account-grid': isSwitchPlan },
                { 'payment-summary__layout': enhancedPaymentSummaryEnabled },
                { '-no-steps-header': !showStepsHeader },
            ]"
            :aa-region="formConfig.aaRegion">
            <!-- tabs menu -->
            <PaymentTabsContainer
                :activeTab="activeTab"
                @update:activeTab="emitActiveTab($event)" />
            <!-- START OF AB TEST PLOCTOPLUS-1915: center alignment payment page -->
            <!-- payment form tab (credit card/paypal): no AB Test, center alignment payment page AB TEST, control, variants A and B -->
            <transition-group
                name="fade"
                tag="div"
                :class="[
                    { ...getPaymentFormTabsModuleClass },
                    { '-no-steps-header': !showStepsHeader },
                ]">
                <PaymentCreditCardTab
                    :key="tab.cc"
                    :activeTab="activeTab"
                    :isEditPayment="isEditPayment"
                    :isFromPaymentSummary="isFromPaymentSummary"
                    :formReference="$refs.paymentForm"
                    :handleProcessedPaymentError="handleProcessedPaymentError"
                    :handleProcessedPayment="handleProcessedPayment"
                    :recaptchaAction="recaptchaAction"
                    :isFTC="isFTC"
                    v-bind:formIsValid.sync="formIsValid" />
                <PaymentPaypalTab
                    :key="tab.payPal"
                    :activeTab="activeTab"
                    :isEditPayment="isEditPayment" />
            </transition-group>

            <input type="hidden" name="recurly-token" data-recurly="token" />

            <div class="grid-item grid-item--row sub-page--actionbar sub-page--actionbar-padded">
                <template v-if="activeTab === tab.cc && isSwitchPlan">
                    <FormButton
                        :buttonConfig="buttonConfig"
                        :buttonState="submitButtonState"
                        @click="onClickSubmit"></FormButton>
                </template>
                <template v-else-if="activeTab === tab.payPal && !paypalTokenIsPresent">
                    <button
                        class="payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                        @click="handlePayPalCheckout($event, paypalGateway)">
                        <span class="label">
                            {{ $getLocale('checkout_with') }}
                        </span>
                        <span class="logo" aria-label="PayPal"></span>
                    </button>
                </template>
            </div>
        </div>
    </form>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex';
    import { SKIP_EXPLAINER_STEPS } from 'aa/helpers/featureConstants';
    import PaymentCreditCardTab from './PaymentCreditCardTab';
    import PaymentPaypalTab from './PaymentPaypalTab';
    import PaymentTabsContainer from './PaymentTabsContainer';
    import { CreditCardPaymentConfig, TAB } from 'aa/vue/FormConfig';
    // START TEST PLOCTOPLUS-1915: center align payment page
    import CenterAlignPaymentPageAbTest from 'aa/vue/abTestRelated/CenterAlignPaymentPageAbTest';
    // END TEST PLOCTOPLUS-1915: center align payment page
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import FormButton from 'atoms/FormButton';
    import ButtonCta from 'atoms/ButtonCta';
    import FormState from 'aa/vue/plugins/FormState';
    import { ACTION_NAMES, PAGE_TYPES, tracking } from 'services/Tracking';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
    import { FTC_COMPLIANCE_ENABLED } from 'helpers/featureConstants';
    import { FLOW_TYPE } from 'models/FlowModel';

    export default {
        name: 'PaymentMethodSelection',
        mixins: [CenterAlignPaymentPageAbTest, FormState, checkoutMixin],
        components: {
            PaymentCreditCardTab,
            PaymentPaypalTab,
            PaymentTabsContainer,
            FormButton,
            ButtonCta,
        },
        props: {
            isEditPayment: {
                type: Boolean,
                default: false,
            },
            isFromPaymentSummary: {
                type: Boolean,
                default: false,
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                enhancedPaymentSummaryEnabled: false,
                featurePaypal: false,
                acceptedCards: [],
                activeTab: TAB.cc,
                countryFormClass: '',
                altPaymentFormView: false,
                payPalToken: null,
                recaptchaAction: this.isSwitchPlan ? 'FORM_EDIT_PAYMENT' : 'FORM_PAYMENT',
                formIsProcessing: false,
                formIsValid: false,
            };
        },
        mounted() {
            this.enhancedPaymentSummaryEnabled =
                this.$store.state.serverData.enhancedPaymentSummaryEnabled;
            if (this.isEditPayment) {
                this.recaptchaAction = 'FORM_EDIT_PAYMENT';
            }
        },
        computed: {
            ...mapGetters(['featureIsActive']),
            ...mapGetters('plan', ['isSwitchPlan']),
            ...mapGetters('ftc', ['getPayPalToken']),
            hasSkipExplainers() {
                return this.featureIsActive(SKIP_EXPLAINER_STEPS);
            },
            showStepsHeader() {
                if (this.$store.state.serverData.hideStepsHeader) {
                    return false;
                }

                return !this.isEditPayment && !this.hasSkipExplainers;
            },
            tab() {
                return TAB;
            },
            cancelConfig() {
                return {
                    type: 'button',
                    styleClass: ['button', 'secondary'],
                    displayText: this.$getLocale('cancel'),
                    event: this.cancelOnClicked,
                };
            },
            formConfig() {
                return CreditCardPaymentConfig;
            },
            paypalGateway() {
                if (this.featurePaypal) {
                    const appMode = this.$store.state.serverData?.appMode;
                    const defaultGatewayList =
                        this.$store.state.serverData.paymentMethods.paypal?.gateway?.default;
                    if (appMode) {
                        return defaultGatewayList ? defaultGatewayList[appMode] : false;
                    }
                    return false;
                }
                return false;
            },
            paypalTokenIsPresent() {
                return this.getPayPalToken?.id;
            },
            trackingService() {
                return this.$trackingService ?? tracking;
            },
            isFTCComplianceEnabled() {
                return this.featureIsActive(FTC_COMPLIANCE_ENABLED);
            },
        },
        methods: {
            ...mapMutations('ftc', ['setFormValidated']),
            emitActiveTab(tab) {
                this.activeTab = tab;
                this.$emit('update:activeTab', tab);
            },

            cancelOnClicked() {
                this.trackingService.trackAction(ACTION_NAMES.CREDIT_CARD_CANCEL);
                this.$router.push({ name: 'AccountMain' });
            },
            submitEditPayment(token) {
                const payload = {
                    token: token.id,
                };

                this.paymentRequestData = payload;
                const httpData = this.buildHttpData(payload);
                this.sendPayment(httpData);
            },

            submitPayment(token, buttonEvent) {
                this.formErrorMessage = null;
                this.formIsProcessing = true;

                let couponCode = null;
                if (this.coupon && this.coupon.recurlyInfo && this.coupon.recurlyInfo.code) {
                    couponCode = this.coupon.recurlyInfo.code;
                }

                let payload = {
                    token: token,
                    buttonEvent: buttonEvent,
                    plan: this.currentPlan,
                    couponCode: couponCode,
                    tk_trp: this.$store.state.token,
                };

                if (this.flowType === FLOW_TYPE.AARP) {
                    let aarpUserId = { aarpUserId: this.getAarpUserId() };
                    Object.assign(payload, aarpUserId);
                }

                // Save payload for retry if needed
                this.paymentRequestData = payload;

                this.isEditPayment &&
                    this.trackingService.trackAction(ACTION_NAMES.CREDIT_CARD_SUBMIT_UPDATE);
                this.sendPayment(payload);
            },

            sendPayment(payload) {
                this.formIsProcessing = true;

                if (
                    this.hasOwnProperty('recaptchaAction') &&
                    this.recaptchaFromActionEnabled(this.recaptchaAction)
                ) {
                    payload.recaptchaAction = this.recaptchaAction;
                }

                const couponCode = payload.couponCode;
                if ('URLSearchParams' in window) {
                    if (location.search) {
                        const searchParams = new URLSearchParams(location.search.substr(1));
                        const gatewayOverride = searchParams.get('gateway');
                        if (gatewayOverride) {
                            payload.gateway = gatewayOverride;
                        }
                    }
                }

                const processSelectedMethod = this.isEditPayment
                    ? this.processEditPayment
                    : this.processPayment;
                processSelectedMethod(payload)
                    .then(async (resp) => {
                        if (!this.isFromPaymentSummary) {
                            this.handleProcessedPayment(resp);
                        }

                        if (!resp.success || !this.isEditPayment) {
                            return;
                        }

                        const purchasePaymentMethod =
                            this.activeTab === this.tab.cc ? 'credit-card' : 'paypal';

                        if (this.isFromPaymentSummary) {
                            await this.syncServerDataWithDeepAssign();
                            this.formIsProcessing = false;
                            this.editPaymentSuccess = true;
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    ctaText: this.buttonConfig.displayText,
                                    pageURL: window.location.href,
                                    pageType: PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                                    purchasePaymentMethod,
                                },
                            );

                            // Timeout to allow the button success state to be displayed before closing the form in the payment summary page
                            setTimeout(() => {
                                this.$emit('update-payment-method-success', resp.success);
                                this.editPaymentSuccess = false;
                            }, 1000);

                            this.openNotification(
                                new NotificationBarModel({
                                    success: true,
                                    message: this.$getLocale(
                                        'your_payment_information_was_successfully_updated',
                                    ),
                                }),
                            );
                        }

                        if (!this.isFromPaymentSummary) {
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    linkName: ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                    pageType: PAGE_TYPES.ACCOUNT_UPDATE_PAYMENT,
                                    pageURL: window.location.href,
                                    pageName: '/account/payment',
                                    purchasePaymentMethod,
                                    optimizelyExp: CBS.Tracking?.utag_data?.optimizelyExp,
                                    userType: this.$store.state.user?.svod?.status,
                                    userStatus: this.$store.getters['user/trackingUserStatus'],
                                    userProfileId: CBS.Registry?.user?.profile_id,
                                    userProfileCategory: CBS.Registry?.user?.profile_type,
                                    userProfilePicPath: CBS.Registry?.user?.userProfilePicPath,
                                    userRegId: this.$store.state?.user?.regID,
                                },
                            );
                        }
                    })
                    .catch((err) => {
                        let trackingParams = {};
                        if (this.activeTab === TAB.payPal) {
                            const { planType } = this.plan;
                            trackingParams.purchasePaymentMethod = this.activeTab;
                            trackingParams.productPricingPlan = planType;
                        }
                        this.handleProcessedPaymentError(err, trackingParams);
                    })
                    .finally(() => {
                        if (this.formIsProcessing) {
                            this.formIsProcessing = false;
                        }
                    });
            },

            setUpThreeDSecure(actionTokenId) {
                if (actionTokenId) {
                    const risk = this.recurly.Risk();

                    const threeDSecure = risk.ThreeDSecure({ actionTokenId });
                    threeDSecure.on('error', (err) => {
                        this.formErrorMessage = this.$getLocale(
                            'recurly_PSD2_error_please_try_again',
                        );
                        this.closeThreeDModal();
                        threeDSecure.remove();
                    });
                    threeDSecure.on('token', (token) => {
                        this.resubmitPayment(token);
                        this.closeThreeDModal();
                        threeDSecure.remove();
                    });
                    this.openThreeDModal();
                    threeDSecure.attach(this.$refs.threeDSecure);
                }
            },
        },
        watch: {
            activeTab(newValue) {
                this.$emit('update-active-tab', newValue);
            },
            formIsValid(newValue) {
                this.setFormValidated(newValue);
            },
        },
    };
</script>
